// App.js
import React, { useMemo, useState, useCallback } from 'react';
import Navbar from './components/Layout/Navbar';
import StudentForm from './components/Student/StudentForm';
import StudentTable from './components/Student/StudentTable';
import BulkUpload from './components/Bulk/BulkUpload';
import ProtectedRoute from './components/Common/ProtectedRoute';
import SchoolManager from './components/Admin/SchoolManager';
import ClassLinkManager from './components/ClassAccess/ClassLinkManager';
import ClassStudentEditor from './components/ClassAccess/ClassStudentEditor';
import Spinner from './components/Common/Spinner';
import './App.css';
import {
  Download,
  List,
  PlusIcon,
  Settings,
  UploadCloud,
  Database,
  Users,
  School,
  Trash2,
  Link2,
} from 'lucide-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import useStudents from './hooks/useStudents';
import { useAuth } from './context/AuthContext';
import { showAlert, showConfirmDialog, showToast } from './utils/feedback';

const EXPORT_HEADER_MAP = {
  export_serial_no: 'SERIAL NO',
  originalPhotoName: 'PHOTO NAME',
  student_name: 'STUDENT NAME',
  fathers_name: 'FATHER NAME',
  dob: 'DATE OF BIRTH',
  std_roman: 'STD',
  division: 'DIVISION',
  custom_div: 'CUSTOM DIVISION',
  reg_no: 'REG NO',
  appar_id: 'PEN NO',
  sat_no: 'SAT NO',
  house: 'HOUSE',
  address: 'ADDRESS',
  mobile: 'MOBILE',
};

const EXPORT_FIELDS = Object.keys(EXPORT_HEADER_MAP);
const EXPORT_HEADERS = Object.values(EXPORT_HEADER_MAP);

function DashboardApp() {
  const { isAdmin, schoolName } = useAuth();
  const [view, setView] = useState(isAdmin ? 'admin' : 'list');
  const [editingStudent, setEditingStudent] = useState(null);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deletingFiltered, setDeletingFiltered] = useState(false);
  const [filteredExportState, setFilteredExportState] = useState({
    students: [],
    filters: {
      searchTerm: '',
      standardFilter: '',
      divisionFilter: '',
    },
  });

  const { students, loading, addStudent, updateStudent, deleteStudent } = useStudents();

  const formInitialData = useMemo(() => editingStudent || undefined, [editingStudent]);

  const handleEdit = (student) => {
    setEditingStudent(student);
    setShowStudentForm(true);
    setView('list');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingStudent(null);
    setShowStudentForm(false);
  };

  const handleOpenAddStudent = () => {
    setEditingStudent(null);
    setShowStudentForm(true);
    setView('list');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmitStudent = async (studentData, photoFile) => {
    if (editingStudent?.id) {
      await updateStudent(editingStudent.id, studentData, photoFile);
      setEditingStudent(null);
      setShowStudentForm(false);
      return;
    }

    await addStudent(studentData, photoFile);
    setShowStudentForm(false);
  };

  const handleFilteredStudentsChange = useCallback((payload) => {
    setFilteredExportState(payload);
  }, []);

  const buildExportRows = (rows) => {
    const sortedRows = [...rows].sort((a, b) => {
      const aDate =
        typeof a?.created_at?.toDate === 'function'
          ? a.created_at.toDate().getTime()
          : new Date(a?.created_at || a?.updated_at || 0).getTime();

      const bDate =
        typeof b?.created_at?.toDate === 'function'
          ? b.created_at.toDate().getTime()
          : new Date(b?.created_at || b?.updated_at || 0).getTime();

      if (!Number.isNaN(aDate) && !Number.isNaN(bDate) && aDate !== bDate) {
        return aDate - bDate;
      }

      const aSerial = Number(a?.serial_no ?? Number.MAX_SAFE_INTEGER);
      const bSerial = Number(b?.serial_no ?? Number.MAX_SAFE_INTEGER);

      if (!Number.isNaN(aSerial) && !Number.isNaN(bSerial) && aSerial !== bSerial) {
        return aSerial - bSerial;
      }

      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });

    return sortedRows.map((student, index) => {
      const exportRow = {};

      EXPORT_FIELDS.forEach((field) => {
        let value = '';

        if (field === 'export_serial_no') {
          value = index + 1;
        } else if (field === 'originalPhotoName') {
          value = student?.originalPhotoName || student?.photo_name || '';
        } else if (field === 'std_roman') {
          const std = String(student?.std_roman || '').trim();
          const division = String(student?.division || '').trim();
          value = std ? (division ? `${std}-${division}` : std) : '';
        } else if (field === 'house') {
          const house = String(student?.house || '').trim();
          value = house ? `${house}.png` : '';
        } else {
          value = student?.[field];
        }

        const excelHeader = EXPORT_HEADER_MAP[field];
        exportRow[excelHeader] = value === undefined || value === null ? '' : value;
      });

      return exportRow;
    });
  };

  const handleDownloadData = async () => {
    const currentRows =
      filteredExportState.students.length > 0 ||
      filteredExportState.filters.standardFilter ||
      filteredExportState.filters.divisionFilter ||
      filteredExportState.filters.searchTerm
        ? filteredExportState.students
        : students;

    const { standardFilter, divisionFilter, searchTerm } = filteredExportState.filters;

    const hasAnyFilter = Boolean(standardFilter) || Boolean(divisionFilter) || Boolean(searchTerm);

    if (!currentRows.length) {
      showAlert({
        title: 'No data available',
        text: 'There are no student records to download.',
        icon: 'info',
      });
      return;
    }

    const confirmResult = await showConfirmDialog({
      title: 'Download student data?',
      text: hasAnyFilter
        ? `Download ${currentRows.length} filtered student record(s).`
        : `No filters applied. Download all ${currentRows.length} student record(s)?`,
      icon: 'question',
      confirmButtonText: 'Download',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#2563eb',
    });

    if (!confirmResult.isConfirmed) return;

    try {
      setDownloading(true);
      const rows = buildExportRows(currentRows);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');

      worksheet.addRow(EXPORT_HEADERS);

      rows.forEach((row) => {
        const orderedRow = EXPORT_HEADERS.map((header) => row[header] ?? '');
        worksheet.addRow(orderedRow);
      });

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

      worksheet.columns.forEach((column) => {
        let maxLength = 12;

        column.eachCell({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value ? String(cell.value) : '';
          maxLength = Math.max(maxLength, cellValue.length);
        });

        column.width = Math.min(maxLength + 2, 40);
      });

      const now = new Date();
      const dateStamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
        now.getDate()
      ).padStart(2, '0')}`;

      const fileSuffix = hasAnyFilter ? 'filtered' : 'all';
      const fileName = `students_${fileSuffix}_${dateStamp}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, fileName);
      showToast({ icon: 'success', title: 'Excel file downloaded successfully.' });
    } catch (error) {
      console.error('Excel export error:', error);
      showToast({ icon: 'error', title: error?.message || 'Failed to download Excel file.' });
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteAllFilteredData = useCallback(async () => {
    const currentRows =
      filteredExportState.students.length > 0 ||
      filteredExportState.filters.standardFilter ||
      filteredExportState.filters.divisionFilter ||
      filteredExportState.filters.searchTerm
        ? filteredExportState.students
        : students;

    const { standardFilter, divisionFilter, searchTerm } = filteredExportState.filters;
    const hasAnyFilter = Boolean(standardFilter) || Boolean(divisionFilter) || Boolean(searchTerm);

    if (!currentRows.length) {
      showAlert({
        title: 'No data available',
        text: 'There are no student records to delete.',
        icon: 'info',
      });
      return;
    }

    const confirmResult = await showConfirmDialog({
      title: 'Delete filtered student data?',
      text: hasAnyFilter
        ? `This will permanently delete ${currentRows.length} filtered student record(s). This action cannot be undone.`
        : `No filters are applied. This will delete all ${currentRows.length} student record(s). Are you sure?`,
      icon: 'warning',
      confirmButtonText: 'Delete All',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
    });

    if (!confirmResult.isConfirmed) return;

    try {
      setDeletingFiltered(true);

      const rowsToDelete = currentRows.filter((student) => student?.id);

      if (!rowsToDelete.length) {
        throw new Error('No valid student IDs found to delete.');
      }

      await Promise.all(rowsToDelete.map((student) => deleteStudent(student.id)));

      setEditingStudent(null);
      setShowStudentForm(false);

      showToast({
        icon: 'success',
        title: `${rowsToDelete.length} student record(s) deleted successfully.`,
      });
    } catch (error) {
      console.error('Bulk delete filtered students error:', error);
      showToast({
        icon: 'error',
        title: error?.message || 'Failed to delete filtered student records.',
      });
    } finally {
      setDeletingFiltered(false);
    }
  }, [filteredExportState, students, deleteStudent]);

  const stats = [
    {
      label: 'Total Records',
      value: students.length,
      icon: <Database size={18} />,
    },
    {
      label: 'Visible Records',
      value: filteredExportState.students.length || students.length,
      icon: <Users size={18} />,
    },
  ];

  const isAdminView = isAdmin;

  return (
    <ProtectedRoute>
      <div className="app-shell">
        <Navbar />

        <main className="app-main container-fluid">
          {!isAdmin && (
            <section className="dashboard-summary mb-4">
              <div className="summary-card summary-card--accent">
                <div className="summary-icon white">
                  <School size={18} />
                </div>
                <div>
                  <div className="summary-label">School Workspace</div>
                  <div className="summary-value text-truncate">{schoolName || 'Dashboard'}</div>
                </div>
              </div>

              {stats.map((item) => (
                <div key={item.label} className="summary-card">
                  <div className="summary-icon">{item.icon}</div>
                  <div>
                    <div className="summary-label">{item.label}</div>
                    <div className="summary-value">{item.value}</div>
                  </div>
                </div>
              ))}
            </section>
          )}

          <div className={`app-grid app-grid--full ${isAdminView ? 'app-grid--full' : ''}`}>
            {!isAdminView && (
              <aside className="app-sidebar">
                {showStudentForm && (
                  <StudentForm
                    onAdd={handleSubmitStudent}
                    initialData={formInitialData}
                    isEditing={Boolean(editingStudent)}
                    onCancelEdit={handleCancelEdit}
                    onSaved={() => {
                      setEditingStudent(null);
                      setShowStudentForm(false);
                    }}
                  />
                )}

                <div className="app-panel side-nav-card">
                  <div className="app-panel-header mb-3">
                    <h3>Quick Actions</h3>
                    <p>Manage records without changing the existing workflow.</p>
                  </div>

                  <div className="quick-actions">
                    <button
                      onClick={() => setView('list')}
                      className={`quick-action-btn ${view === 'list' ? 'active' : ''}`}
                    >
                      <List size={18} />
                      <span>Student Records</span>
                    </button>

                    <button
                      onClick={handleOpenAddStudent}
                      className={`quick-action-btn ${showStudentForm && !editingStudent ? 'active' : ''}`}
                    >
                      <PlusIcon size={18} />
                      <span>Add New Student</span>
                    </button>

                    <button
                      onClick={handleDownloadData}
                      className="quick-action-btn"
                      disabled={downloading}
                    >
                      {downloading ? <Spinner label="Preparing export..." small /> : <Download size={18} />}
                      {!downloading && <span>Download Data</span>}
                    </button>

                    <button
                      onClick={handleDeleteAllFilteredData}
                      className="quick-action-btn quick-action-btn--danger"
                      disabled={deletingFiltered}
                    >
                      {deletingFiltered ? <Spinner label="Deleting..." small /> : <Trash2 size={18} />}
                      {!deletingFiltered && <span>Delete Filtered</span>}
                    </button>

                    <button
                      onClick={() => setView('bulk')}
                      className={`quick-action-btn ${view === 'bulk' ? 'active' : ''}`}
                    >
                      <UploadCloud size={18} />
                      <span>Bulk Import</span>
                    </button>

                    <button
                      onClick={() => setView('class-links')}
                      className={`quick-action-btn ${view === 'class-links' ? 'active' : ''}`}
                    >
                      <Link2 size={18} />
                      <span>Class Logins</span>
                    </button>
                  </div>
                </div>
              </aside>
            )}

            <section className="app-content">
              {isAdminView ? (
                <div className="page-section">
                  <div className="app-panel">
                    <div className="section-heading">
                      <div className="section-heading-icon">
                        <Settings size={20} />
                      </div>
                      <div>
                        <h2>Admin School Management</h2>
                        <p>Create schools, control permissions, and jump into school sessions.</p>
                      </div>
                    </div>
                  </div>

                  <SchoolManager />
                </div>
              ) : view === 'list' ? (
                <div className="page-section">
                  <div className="section-heading mb-3">
                    <div>
                      <h2>Student Database</h2>
                      <p>Responsive record view with better search, feedback, and action flow.</p>
                    </div>
                  </div>

                  <StudentTable
                    students={students}
                    loading={loading}
                    onDelete={deleteStudent}
                    onEdit={handleEdit}
                    onFilteredStudentsChange={handleFilteredStudentsChange}
                  />
                </div>
              ) : view === 'class-links' ? (
                <div className="page-section">
                  <div className="section-heading mb-3">
                    <div>
                      <h2>Class Login Links</h2>
                      <p>Create and manage passwordless teacher links for each class.</p>
                    </div>
                  </div>

                  <ClassLinkManager students={students} />
                </div>
              ) : (
                <div className="page-section">
                  <div className="section-heading mb-3">
                    <div>
                      <h2>Administrative Tools</h2>
                      <p>Bulk import workflow with progress feedback and improved upload states.</p>
                    </div>
                  </div>
                  <BulkUpload addStudent={addStudent} />
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </ProtectedRoute>
  );
}

function App() {
  const classAccessMatch = window.location.pathname.match(/^\/class-access\/([^/]+)\/?$/);

  if (classAccessMatch) {
    return <ClassStudentEditor token={decodeURIComponent(classAccessMatch[1])} />;
  }

  return <DashboardApp />;
}

export default App;